package ru.villawonka.app.presentation.screens.postCard.compose.view

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.Divider
import androidx.compose.material.Icon
import androidx.compose.material.MaterialTheme
import androidx.compose.material.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import org.jetbrains.compose.resources.painterResource
import ru.villawonka.app.common.extensions.priceFormat
import ru.villawonka.app.presentation.design.components.AppFilledChip
import ru.villawonka.app.presentation.design.extensions.AppSpacer
import ru.villawonka.app.presentation.design.theme.*
import ru.villawonka.app.presentation.model.AccommodationAdditionUIModel
import ru.villawonka.app.presentation.model.PostUIModel
import ru.villawonka.app.resources.AppResources
import villawonka.composeapp.generated.resources.Res
import villawonka.composeapp.generated.resources.ic_nav_arrow_down_24
import villawonka.composeapp.generated.resources.ic_nav_arrow_up_24

enum class PostDetailsCardTab {
    DESCRIPTION,
    RENT,
    ADDITIONS,
}

@Composable
fun PostDetailsCardView(post: PostUIModel) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(
                color = MaterialTheme.colors.surfaceMiddle,
                shape = RoundedCornerShape(16.dp),
            )
    ) {
        var selectedTab by remember { mutableStateOf(PostDetailsCardTab.DESCRIPTION) }

        AppSpacer(16.dp)
        Row(Modifier.horizontalScroll(rememberScrollState())) {
            AppSpacer(16.dp)
            PostDetailsCardTab.entries.forEach { tab ->
                AppFilledChip(
                    text = when (tab) {
                        PostDetailsCardTab.DESCRIPTION ->
                            AppResources.strings.postCardDescriptionTab
                        PostDetailsCardTab.RENT ->
                            AppResources.strings.postCardRentTab
                        PostDetailsCardTab.ADDITIONS ->
                            AppResources.strings.postCardAmenitiesTab
                    },
                    focused = selectedTab == tab,
                ) {
                    selectedTab = tab
                }
                AppSpacer(12.dp)
            }
        }
        AppSpacer(16.dp)
        Column(modifier = Modifier.padding(horizontal = 16.dp)) {
            when (post) {
                is PostUIModel.Accommodation -> when (selectedTab) {
                    PostDetailsCardTab.DESCRIPTION -> {
                        DescriptionView(post)
                        AppSpacer(8.dp)
                    }
                    PostDetailsCardTab.RENT -> {
                        RentView(post)
                    }
                    PostDetailsCardTab.ADDITIONS -> {
                        AdditionsView(post)
                    }
                }
            }
        }
        AppSpacer(16.dp)
    }
}

@Composable
private fun DescriptionView(post: PostUIModel) {
    Text(
        text = post.description,
        style = AppTextStyles.bodySmallRegular
    )
}

@Composable
private fun RentView(post: PostUIModel.Accommodation) {
    ExpandableTableView(
        buildMap {
            put(post.accommodationType.key, post.accommodationType.value)
            put(post.rentType.key, post.rentType.value)
            put(AppResources.strings.postCardRentCost, "${post.displayCost.priceFormat()} ${post.displayCurrency.code}")
            post.displayDeposit?.let {
                put(AppResources.strings.postCardRentDeposit, "${post.displayDeposit.priceFormat()} ${post.displayCurrency.code}")
            }
            post.rentFrom?.let {
                put(AppResources.strings.postCardRentFrom, it)
            }
            post.rentTo?.let {
                put(AppResources.strings.postCardRentTo, it)
            }
            post.additions.find { it is AccommodationAdditionUIModel.UtilityBillsIncluded }?.let {
                put(it.key, it.value)
            }
        }
    )
}

@Composable
private fun AdditionsView(post: PostUIModel.Accommodation) {
    ExpandableTableView(
        buildMap {
            post.additions
                .filter { it !is AccommodationAdditionUIModel.UtilityBillsIncluded }
                .forEach { put(it.key, it.value) }
        }
    )
}

@Composable
private fun ExpandableTableView(map: Map<String, String>) {
    val shownItemCount = 3
    val isNeedShowExpand = map.entries.size > shownItemCount
    var isExpanded by remember { mutableStateOf(!isNeedShowExpand) }
    val items = if (isExpanded) map.entries else map.entries.take(shownItemCount)

    Column {
        items.forEachIndexed { index, entry ->
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier
                    .fillMaxWidth()
                    .height(36.dp),
            ) {
                Text(
                    modifier = Modifier.weight(1f),
                    text = entry.key,
                    style = AppTextStyles.bodySmallMedium,
                    color = MaterialTheme.colors.onSurfaceSecondary,
                )
                Text(
                    modifier = Modifier.weight(1f),
                    text = entry.value,
                    style = AppTextStyles.bodySmallMedium,
                    color = MaterialTheme.colors.onSurfacePrimary,
                )
            }
            if (index != items.size - 1) {
                Divider(
                    color = MaterialTheme.colors.outlinePrimary
                )
            }
        }
        if (isNeedShowExpand) {
            AppSpacer(8.dp)
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.clickable(
                    interactionSource = null,
                    indication = null
                ) { isExpanded = !isExpanded }
            ) {
                if (isExpanded) {
                    Text(
                        text = AppResources.strings.postCardHide,
                        style = AppTextStyles.bodySmallBold,
                        color = MaterialTheme.colors.filledButtonSecondaryContentInverted,
                    )
                    AppSpacer(4.dp)
                    Icon(
                        painter = painterResource(Res.drawable.ic_nav_arrow_up_24),
                        contentDescription = null,
                        tint = MaterialTheme.colors.filledButtonSecondaryContentInverted
                    )
                } else {
                    Text(
                        text = AppResources.strings.postCardMore,
                        style = AppTextStyles.bodySmallBold,
                        color = MaterialTheme.colors.filledButtonSecondaryContentInverted,
                    )
                    AppSpacer(4.dp)
                    Icon(
                        painter = painterResource(Res.drawable.ic_nav_arrow_down_24),
                        contentDescription = null,
                        tint = MaterialTheme.colors.filledButtonSecondaryContentInverted
                    )
                }
            }
        }
    }
}